import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { World } from '../types';
import { deleteWorld } from '../api';
import { useWorldsStore, useAuthStore } from '../store';

interface WorldListProps {
  worlds: World[];
  onCreateNew: () => void;
  onImportWorld?: () => void;
}

export function WorldList({ worlds, onCreateNew, onImportWorld }: WorldListProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const removeWorld = useWorldsStore(state => state.removeWorld);
  const user = useAuthStore(state => state.user);
  const isAdmin = !!user?.is_admin;
  const isVerified = isAdmin || !!user?.email_verified;
  // A branch names its source while the source is still in the list.
  const titles = new Map(worlds.map(world => [world.id, world.title]));
  const branchSources = new Map(worlds.flatMap(world => {
    const source = world.branched_from_world_id && titles.get(world.branched_from_world_id);
    return source ? [[world.id, source] as const] : [];
  }));
  
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  
  const handleDelete = async (worldId: string, e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();
    setConfirmDeleteId(worldId);
  };
  
  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    
    setDeletingId(confirmDeleteId);
    try {
      await deleteWorld(confirmDeleteId);
      removeWorld(confirmDeleteId);
    } catch (error) {
      console.error('Failed to delete world:', error);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-dark-50 mb-2">Delete World?</h3>
            <p className="text-dark-300 mb-6">
              This will permanently delete this world and all its messages. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 bg-dark-600 text-dark-50 rounded hover:bg-dark-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingId === confirmDeleteId}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500 transition-colors disabled:opacity-50"
              >
                {deletingId === confirmDeleteId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <NewWorldDropdown onCreateNew={onCreateNew} onImportWorld={onImportWorld} isVerified={isVerified} />

      {worlds.length === 0 ? (
        <div className="text-center py-12 bg-dark-700 rounded-lg">
          <div className="mb-4 flex justify-center">
            <img src="/vite.svg" alt="Textborne logo" className="h-10 w-10" />
          </div>
          <h3 className="text-lg font-medium text-dark-50 mb-2">No worlds yet</h3>
          <p className="text-dark-200 mb-4">Create your first narrative world to begin your story</p>
          <button
            onClick={onCreateNew}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isVerified}
            title={!isVerified ? 'Verify your email to create worlds' : undefined}
          >
            Create Your First World
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {worlds.map(world => (
            <div
              key={world.id}
              className="bg-dark-700 rounded-lg p-4 border border-dark-500 hover:border-primary-500 transition-colors"
            >
              <div className="flex items-start justify-between">
                <Link to={`/world/${world.id}`} className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-dark-50 hover:text-primary-400 transition-colors">
                    {world.title}
                  </h3>
                  <p className="text-dark-300 text-sm mt-1">
                    {world.message_count} messages • Created {formatDate(world.created_at)}
                    {branchSources.get(world.id) && <> • Branched from {branchSources.get(world.id)}</>}
                  </p>
                </Link>
                <div className="flex items-center gap-2 ml-4">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    world.status === 'active' 
                      ? 'bg-green-900/50 text-green-300' 
                      : 'bg-dark-500 text-dark-200'
                  }`}>
                    {world.status}
                  </div>
                  <button
                    onClick={(e) => handleDelete(world.id, e)}
                    className="p-1.5 text-dark-400 hover:text-red-400 hover:bg-dark-600 rounded transition-colors"
                    title="Delete world"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewWorldDropdown({ onCreateNew, onImportWorld, isVerified = true }: { onCreateNew: () => void; onImportWorld?: () => void; isVerified?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!onImportWorld) {
    return (
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-dark-50">Your Worlds</h2>
        <button
          onClick={onCreateNew}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!isVerified}
          title={!isVerified ? 'Verify your email to create worlds' : undefined}
        >
          + New World
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-semibold text-dark-50">Your Worlds</h2>
      <div className="relative" ref={ref}>
        <button
          onClick={() => isVerified && setOpen(!open)}
          className={`btn-primary flex items-center gap-1 ${!isVerified ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={!isVerified}
          title={!isVerified ? 'Verify your email to create worlds' : undefined}
        >
          + New World
          <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-dark-700 border border-dark-500 rounded-lg shadow-lg z-20 py-1">
            <button
              onClick={() => { setOpen(false); onCreateNew(); }}
              className="w-full px-3 py-2 text-left text-sm text-dark-200 hover:bg-dark-600 hover:text-dark-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New
            </button>
            <button
              onClick={() => { setOpen(false); onImportWorld(); }}
              className="w-full px-3 py-2 text-left text-sm text-dark-200 hover:bg-dark-600 hover:text-dark-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Import from Chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
